import React, { useState } from 'react';
import {
    Box, Button, Container, Typography, Stack, AppBar, Toolbar,
    Paper, Grid, useTheme, useMediaQuery, Fade, Zoom
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Layout/Footer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Security as SecurityIcon } from '@mui/icons-material';
import SpeedIcon from '@mui/icons-material/Speed';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import GroupsIcon from '@mui/icons-material/Groups';
import BookDemoModal from '../components/Landing/BookDemoModal';

const LaptopMockup = ({ image }: { image: string }) => (
    <Box sx={{
        position: 'relative',
        width: '100%',
        maxWidth: { xs: '100%', lg: '1000px' },
        margin: '0 auto',
        filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.6))',
        transition: 'transform 0.5s ease-out'
    }}>
        {/* Screen Container */}
        <Box sx={{
            border: { xs: '8px solid #2d3748', md: '12px solid #2d3748' },
            borderRadius: { xs: '16px 16px 0 0', md: '24px 24px 0 0' },
            bgcolor: '#1a202c',
            overflow: 'hidden',
            aspectRatio: '16/10',
            position: 'relative'
        }}>
            <img src={image} alt="Dashboard Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
        {/* Base */}
        <Box sx={{
            height: { xs: '8px', md: '14px' },
            bgcolor: '#4a5568',
            borderRadius: '0 0 12px 12px',
            position: 'relative',
            '&:after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: { xs: '60px', md: '120px' },
                height: '4px',
                bgcolor: '#2d3748',
                borderRadius: '0 0 4px 4px'
            }
        }} />
    </Box>
);

const FeatureCard = ({ icon, title, text, delay = 0 }: { icon: React.ReactNode, title: string, text: string, delay?: number }) => (
    <Fade in={true} timeout={1000} style={{ transitionDelay: `${delay}ms` }}>
        <Paper
            elevation={0}
            sx={{
                p: { xs: 3, md: 4 },
                height: '100%',
                bgcolor: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: { xs: 4, md: 6 },
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                cursor: 'default',
                '&:hover': {
                    transform: 'translateY(-10px) scale(1.02)',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,171,0,0.4)',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.6)',
                    '& .icon-box': {
                        bgcolor: '#ffab00',
                        color: 'black',
                        transform: 'rotate(10deg)'
                    }
                }
            }}
        >
            <Box className="icon-box" sx={{
                color: '#ffab00',
                mb: 3,
                bgcolor: 'rgba(255,171,0,0.1)',
                width: 56,
                height: 56,
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
            }}>
                {icon}
            </Box>
            <Typography variant="h6" color="white" fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.5px' }}>{title}</Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.5)" lineHeight={1.8}>{text}</Typography>
        </Paper>
    </Fade>
);

const LandingPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [demoOpen, setDemoOpen] = useState(false);

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#0B0F19', overflowX: 'hidden' }}>
            {/* Background Effects */}
            <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: 'none' }}>
                <Box sx={{
                    position: 'absolute', top: '-10%', left: '10%', width: '40%', height: '40%',
                    background: 'radial-gradient(circle, rgba(29, 78, 216, 0.15) 0%, transparent 70%)', filter: 'blur(80px)'
                }} />
                <Box sx={{
                    position: 'absolute', bottom: '10%', right: '10%', width: '30%', height: '30%',
                    background: 'radial-gradient(circle, rgba(255, 171, 0, 0.08) 0%, transparent 70%)', filter: 'blur(60px)'
                }} />
            </Box>

            {/* Header */}
            <AppBar position="fixed" color="transparent" elevation={0} sx={{ py: { xs: 1, md: 1.5 }, backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.05)', zIndex: 1100 }}>
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, md: 1.5 }, cursor: 'pointer', minWidth: 0 }} onClick={() => navigate('/')}>
                            <img src="/accounting logo bg.png" alt="Logo" style={{ width: isSmallMobile ? 32 : 38, height: isSmallMobile ? 32 : 38, objectFit: 'contain' }} />
                            <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight={900} color="white" sx={{ letterSpacing: '-1.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>SquareEdge</Typography>
                        </Box>

                        <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center">
                            {!isMobile && (
                                <Button
                                    color="inherit"
                                    onClick={() => navigate('/login')}
                                    sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, textTransform: 'none', px: 3, '&:hover': { color: 'white' } }}
                                >
                                    Log in
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={() => navigate('/school-signup')}
                                size={isSmallMobile ? "small" : "medium"}
                                sx={{
                                    borderRadius: '12px', bgcolor: '#ffab00', color: 'black', fontWeight: 800, textTransform: 'none', px: { xs: 2, md: 3 },
                                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                                    whiteSpace: 'nowrap',
                                    '&:hover': { bgcolor: '#ffca28', transform: 'translateY(-2px)' }, transition: 'all 0.2s'
                                }}
                            >
                                Start 14-Day Free Trial
                            </Button>
                        </Stack>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Hero Section */}
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, pt: { xs: 16, md: 24 }, pb: { xs: 8, md: 12 }, px: { xs: 2, md: 6 } }}>
                <Grid container spacing={{ xs: 6, lg: 12 }} alignItems="center">
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <Stack spacing={4} alignItems={{ xs: 'center', lg: 'flex-start' }} textAlign={{ xs: 'center', lg: 'left' }}>
                            <Zoom in={true} timeout={800}>
                                <Box sx={{ px: 2, py: 0.8, borderRadius: '100px', border: '1px solid rgba(255,171,0,0.3)', bgcolor: 'rgba(255,171,0,0.05)', display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 8, height: 8, bgcolor: '#ffab00', borderRadius: '50%', boxShadow: '0 0 10px #ffab00' }} />
                                    <Typography variant="caption" color="#ffab00" fontWeight={800} letterSpacing={1} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>V2.4 NOW LIVE: DYNAMIC RBAC</Typography>
                                </Box>
                            </Zoom>

                            <Typography variant="h1" color="white" fontWeight={900} sx={{ fontSize: { xs: '2.8rem', sm: '4rem', md: '5rem', xl: '6.5rem' }, lineHeight: 1, letterSpacing: '-3px' }}>
                                Master Your School's <br />
                                <span style={{ background: 'linear-gradient(to right, #ffab00, #ffca28)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Financial Destiny.</span>
                            </Typography>

                            <Typography variant="h5" color="rgba(255,255,255,0.6)" sx={{ maxWidth: 650, lineHeight: 1.6, fontWeight: 400, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                                The world's most precise accounting engine for progressive schools. Eliminate errors, automate term billing, and gain instant corporate-level intelligence.
                            </Typography>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/school-signup')}
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{ borderRadius: '14px', py: { xs: 2, md: 2.2 }, px: 5, fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 800, bgcolor: '#ffab00', color: 'black', '&:hover': { bgcolor: '#ffca28' } }}
                                >
                                    Start 14-Day Free Trial
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => navigate('/login')}
                                    sx={{ borderRadius: '14px', py: { xs: 2, md: 2.2 }, px: 5, fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 700, borderColor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.05)' } }}
                                >
                                    Sign In
                                </Button>
                            </Stack>
                        </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 6 }}>
                        <Box sx={{ position: 'relative', width: '100%' }}>
                            <Box sx={{
                                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                width: '130%', height: '130%', bgcolor: 'rgba(29, 78, 216, 0.1)', filter: 'blur(70px)', borderRadius: '50%', zIndex: -1
                            }} />
                            <LaptopMockup image="/images/mockups/intelligence.png" />
                        </Box>
                    </Grid>
                </Grid>

                {/* Trust Section */}
                <Box sx={{ mt: { xs: 10, md: 15 }, py: 6, borderTop: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
                    <Typography variant="overline" color="rgba(255,255,255,0.4)" fontWeight={800} letterSpacing={3}>POWERING TOMORROW'S INSTITUTIONS</Typography>
                    <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mt: 4, opacity: 0.6, filter: 'grayscale(1)' }} justifyContent="center">
                        {['Academy', 'Polytechnic', 'University', 'College', 'Grammar'].map(name => (
                            <Grid size="auto" key={name}><Typography variant={isSmallMobile ? "h6" : "h5"} color="white" fontWeight={800}>{name}</Typography></Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Features Section */}
                <Box sx={{ mt: { xs: 15, md: 20 } }}>
                    <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: { xs: 6, md: 10 } }}>
                        <Typography variant="h3" color="white" fontWeight={900} sx={{ letterSpacing: '-1.5px', fontSize: { xs: '2rem', md: '3rem' } }}>Built for Absolute Efficiency.</Typography>
                        <Typography variant="h6" color="rgba(255,255,255,0.5)" sx={{ maxWidth: 700, fontSize: { xs: '1rem', md: '1.25rem' } }}>Beyond simple accounting. A complete institutional nervous system designed for growth and transparency.</Typography>
                    </Stack>

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FeatureCard icon={<AccountBalanceIcon fontSize="large" />} title="Master Ledger" text="Automated asset and liability tracking with real-time balance sheet updates." delay={100} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FeatureCard icon={<AnalyticsIcon fontSize="large" />} title="Financial IQ" text="Predictive analytics and cashflow forecasting to keep your school ahead." delay={200} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FeatureCard icon={<GroupsIcon fontSize="large" />} title="Stakeholder Portal" text="Seamless transparency for parents, teachers, and admins in one unified view." delay={300} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FeatureCard icon={<SecurityIcon fontSize="large" />} title="Audit Shield" text="Every transaction is logged with immutable audit trails. Built for clean external audits." delay={400} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FeatureCard icon={<SpeedIcon fontSize="large" />} title="Bulk Billing Engine" text="Generate 5,000+ termly invoices in under 3 minutes. Zero human intervention required." delay={500} />
                        </Grid>
                    </Grid>
                </Box>

                {/* Secondary Showcase - Tablet View */}
                <Box sx={{ mt: { xs: 15, md: 20 }, p: { xs: 3, md: 8 }, borderRadius: { xs: 6, md: 10 }, background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.5) 100%)', border: '1px solid rgba(255,255,255,0.1)', position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: '-20%', right: '-10%', width: '50%', height: '100%', background: 'radial-gradient(circle, rgba(255, 171, 0, 0.1) 0%, transparent 70%)', filter: 'blur(50px)' }} />
                    <Grid container spacing={6} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="h3" color="white" fontWeight={900} gutterBottom sx={{ letterSpacing: '-1px', fontSize: { xs: '2.2rem', md: '3rem' } }}>Mobile First <br />Financial Mastery.</Typography>
                            <Typography variant="h6" color="rgba(255,255,255,0.6)" sx={{ mb: 4, fontWeight: 400, fontSize: { xs: '1rem', md: '1.25rem' } }}>Access your school's vitals from any device. Our responsive engine ensures you never lose sight of your cash position.</Typography>
                            <Stack spacing={2}>
                                {[
                                    '1-Click Fee Receipting',
                                    'Live Student Financial Passports',
                                    'Instant WhatsApp Invoice Delivery'
                                ].map(item => (
                                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ width: 6, height: 6, bgcolor: '#ffab00', borderRadius: '50%' }} />
                                        <Typography color="white" fontWeight={600}>{item}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{
                                border: { xs: '10px solid #2d3748', md: '16px solid #2d3748' },
                                borderRadius: { xs: 4, md: 8 },
                                bgcolor: '#1a202c',
                                overflow: 'hidden',
                                boxShadow: '0 40px 80px rgba(0,0,0,0.8)',
                                transform: { xs: 'none', md: 'rotate(-2deg)' }
                            }}>
                                <img src="/images/mockups/invoices.png" alt="Invoices" style={{ width: '100%', display: 'block' }} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Final CTA */}
                <Box sx={{ mt: 20, textAlign: 'center', py: 10 }}>
                    <Typography variant="h2" color="white" fontWeight={900} sx={{ letterSpacing: '-2px', mb: 3 }}>Ready to transform your school?</Typography>
                    <Typography variant="h6" color="rgba(255,255,255,0.5)" sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>Join 100+ institutions already scaling with SquareEdge. Setup takes less than 24 hours.</Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/school-signup')}
                        sx={{
                            borderRadius: '16px', py: 2.5, px: 8, fontSize: '1.3rem', fontWeight: 900,
                            bgcolor: '#ffab00', color: 'black', boxShadow: '0 20px 40px rgba(255,171,0,0.2)',
                            '&:hover': { bgcolor: '#ffca28', transform: 'translateY(-5px)' }, transition: 'all 0.3s'
                        }}
                    >
                        Start Your Free Trial
                    </Button>
                </Box>
            </Container>

            <Footer />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    );
};

export default LandingPage;
